/**
 * キャンペーン ホワイトボード本体。
 * 仕様書: docs/01_spec/spec_line_bot.md A-11.5（Phase 23a MVP — 線は可視化のみ）
 *
 * 無限ループ防止:
 *   - ノード位置の Firestore 書込は debounce 500ms
 *   - 接続変更も同じ debounce で 1 度の update に集約
 *   - useCampaign は useQuery（onSnapshot 不使用）
 *   - state は React Flow の内部 state、保存タイミングは debounce + 明示「保存」ボタン
 */
import {
  Background,
  Controls,
  MiniMap,
  ReactFlow,
  ReactFlowProvider,
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  useReactFlow,
  type Connection,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  Bookmark,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Maximize2,
  Minimize2,
  Pause,
  Play,
  Plus,
  Save,
  Trash2,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input, Label, Textarea } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import {
  useCampaign,
  useDeleteCampaign,
  useUpdateCampaign,
} from '@/hooks/useCampaigns';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { AddCardMenu } from './AddCardMenu';
import { CampaignMonitoringView } from './CampaignMonitoringView';
import { CardDetailDrawer } from './CardDetailDrawer';
import { ConnectionDetailDrawer } from './ConnectionDetailDrawer';
import { CampaignNode } from './nodes/CampaignNode';

import type {
  BackgroundFieldType,
  BackgroundInfoValue,
  CampaignBackgroundInfo,
  CampaignBackgroundSchema,
  CampaignConnection,
  CampaignElement,
  CampaignElementType,
} from '@goenhub/shared';
import {
  BACKGROUND_GROUP_PRESETS,
  CAMPAIGN_BACKGROUND_DEFAULT_KEYS,
  CAMPAIGN_BACKGROUND_PLACEHOLDERS,
  createEmptyBackgroundInfo,
  expandGroupPreset,
  generateBackgroundKey,
  getCampaignBackgroundLabel,
  resolveBackgroundLabel,
} from '@goenhub/shared';

const NODE_TYPES = { campaign: CampaignNode };

/**
 * standalone（グループ非所属）の任意キーのみを順序通りに返す。
 */
function standaloneCustomKeys(
  meta: CampaignBackgroundInfo,
  schema: CampaignBackgroundSchema,
): string[] {
  const customKeys = Object.keys(meta).filter(
    (k) =>
      !(CAMPAIGN_BACKGROUND_DEFAULT_KEYS as readonly string[]).includes(k) &&
      !schema.fields?.[k]?.groupId,
  );
  // schema.fieldOrder で並び替え（fieldOrder にない key は末尾）
  const orderIdx = new Map<string, number>();
  (schema.fieldOrder ?? []).forEach((k, i) => orderIdx.set(k, i));
  return customKeys.sort((a, b) => {
    const ia = orderIdx.has(a) ? orderIdx.get(a)! : Number.MAX_SAFE_INTEGER;
    const ib = orderIdx.has(b) ? orderIdx.get(b)! : Number.MAX_SAFE_INTEGER;
    return ia - ib;
  });
}

/**
 * 指定 groupId に属する key を groupId 内の順序で返す。
 */
function groupFieldKeys(
  meta: CampaignBackgroundInfo,
  schema: CampaignBackgroundSchema,
  groupId: string,
): string[] {
  return Object.keys(meta).filter(
    (k) => schema.fields?.[k]?.groupId === groupId,
  );
}

function genId(): string {
  return `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function elementsToNodes(
  elements: CampaignElement[],
  handlers: { onOpen: (id: string) => void; onDelete: (id: string) => void },
): Node[] {
  return elements.map((el) => ({
    id: el.id,
    type: 'campaign',
    position: { x: el.x, y: el.y },
    data: { element: el, ...handlers },
  }));
}

const TRIGGER_LABEL_JA: Record<string, string> = {
  on_complete: '完了したら',
  on_form_submit: '回答されたら',
  on_open: '開封されたら',
  on_link_click: 'クリックされたら',
  on_event_start: 'イベント開始で',
  on_event_end: 'イベント終了で',
  after_delay: '時間経過で',
  manual: '手動実行',
};

const CONDITION_LABEL_JA: Record<string, string> = {
  has_tag: 'タグ一致のみ',
  in_group: 'グループ内のみ',
  form_answered: '回答済のみ',
  not_form_answered: '未回答のみ',
};

function formatDelayShort(ms: number): string {
  if (ms <= 0) return '0';
  const day = 24 * 60 * 60 * 1000;
  if (ms % day === 0) return `${ms / day}日後`;
  const hr = 60 * 60 * 1000;
  if (ms % hr === 0) return `${ms / hr}時間後`;
  const min = 60 * 1000;
  if (ms % min === 0) return `${ms / min}分後`;
  return `${Math.round(ms / 1000)}秒後`;
}

function edgeLabelFor(c: CampaignConnection): string {
  if (c.label && c.label.trim()) return c.label;
  const trig = c.trigger?.kind ?? 'on_complete';
  let main: string;
  if (trig === 'after_delay' && c.trigger?.delayMs) {
    main = formatDelayShort(c.trigger.delayMs);
  } else {
    main = TRIGGER_LABEL_JA[trig] ?? trig;
  }
  if (c.condition && c.condition.kind !== 'always') {
    const condLabel = CONDITION_LABEL_JA[c.condition.kind] ?? c.condition.kind;
    return `${main} ｜ ${condLabel}`;
  }
  return main;
}

function connectionsToEdges(connections: CampaignConnection[]): Edge[] {
  return connections.map((c) => ({
    id: c.id,
    source: c.fromId,
    target: c.toId,
    sourceHandle: c.fromHandle,
    targetHandle: c.toHandle,
    label: edgeLabelFor(c),
    labelStyle: { fontSize: 10, fill: 'var(--text-muted)' },
    labelBgStyle: { fill: 'var(--bg-elevated)' },
    style: { stroke: 'var(--text-muted)' },
    // 23a-ext-2: trigger / condition を edge.data に round-trip させる
    data: {
      trigger: c.trigger ?? { kind: 'on_complete' },
      condition: c.condition,
      label: c.label,
    },
  }));
}

function nodesToElements(
  nodes: Node[],
  prevElements: CampaignElement[],
): CampaignElement[] {
  // Node の position を反映、それ以外は prev から保持
  return nodes.map((n) => {
    const prev = prevElements.find((e) => e.id === n.id);
    return {
      ...prev,
      ...((n.data as { element: CampaignElement })?.element ?? {}),
      id: n.id,
      x: n.position.x,
      y: n.position.y,
    } as CampaignElement;
  });
}

function edgesToConnections(edges: Edge[]): CampaignConnection[] {
  return edges.map((e) => {
    const data = (e.data ?? {}) as Partial<
      Pick<CampaignConnection, 'trigger' | 'condition' | 'label'>
    >;
    return {
      id: e.id,
      fromId: e.source,
      toId: e.target,
      fromHandle: (e.sourceHandle as 'left' | 'right' | undefined) ?? 'right',
      toHandle: (e.targetHandle as 'left' | 'right' | undefined) ?? 'left',
      trigger: data.trigger ?? { kind: 'on_complete' },
      condition: data.condition,
      label: data.label,
    };
  });
}

function CampaignBoardInner() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: campaign, isLoading } = useCampaign(id);
  const updateMutation = useUpdateCampaign();
  const deleteMutation = useDeleteCampaign();

  const [name, setName] = useState('');
  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [dirty, setDirty] = useState(false);
  const [drawerOpenId, setDrawerOpenId] = useState<string | null>(null);
  const [edgeDrawerOpenId, setEdgeDrawerOpenId] = useState<string | null>(null);

  // React Flow の内部 state を直接参照するための hook。
  // 外部 useState (nodes, edges) と内部 store が一時的にズレる場合
  // （ドラッグ中・API 直接呼び出し・自動化テスト等）に備え、
  // 保存時は内部 state を真の値として参照する。
  const rf = useReactFlow();

  // キャンペーン詳細（動的キー値オブジェクト、Phase 0）
  const [meta, setMeta] = useState<CampaignBackgroundInfo>(
    createEmptyBackgroundInfo(),
  );
  const [metaSchema, setMetaSchema] = useState<CampaignBackgroundSchema>({});
  const [sidePanelOpen, setSidePanelOpen] = useState(true);
  const [panelWide, setPanelWide] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);

  const updateMetaField = (key: string, value: BackgroundInfoValue) => {
    setMeta((m) => ({ ...m, [key]: value }));
    setDirty(true);
  };
  const removeMetaField = (key: string) => {
    if ((CAMPAIGN_BACKGROUND_DEFAULT_KEYS as readonly string[]).includes(key)) {
      toast.error('既定フィールドは削除できません');
      return;
    }
    setMeta((m) => {
      const next = { ...m };
      delete next[key];
      return next;
    });
    setMetaSchema((s) => {
      const newFields = { ...(s.fields ?? {}) };
      delete newFields[key];
      return {
        ...s,
        fields: newFields,
        fieldOrder: (s.fieldOrder ?? []).filter((k) => k !== key),
      };
    });
    setDirty(true);
  };

  /** フィールド label の編集（任意キーのみ） */
  const renameField = (key: string, label: string) => {
    if ((CAMPAIGN_BACKGROUND_DEFAULT_KEYS as readonly string[]).includes(key)) {
      return; // 既定キーは label 変更不可
    }
    setMetaSchema((s) => ({
      ...s,
      fields: {
        ...(s.fields ?? {}),
        [key]: { ...(s.fields?.[key] ?? {}), label },
      },
    }));
    setDirty(true);
  };

  /** standalone 任意フィールド追加（label は日本語自由入力、key は自動採番） */
  const addStandaloneField = (type: BackgroundFieldType) => {
    const key = generateBackgroundKey('f');
    const defaultLabel =
      type === 'long'
        ? '長文項目'
        : type === 'number'
          ? '数値項目'
          : type === 'date'
            ? '日付項目'
            : '新しい項目';
    const initial: BackgroundInfoValue = type === 'number' ? null : '';
    setMeta((m) => ({ ...m, [key]: initial }));
    setMetaSchema((s) => ({
      ...s,
      fields: {
        ...(s.fields ?? {}),
        [key]: { label: defaultLabel, type },
      },
      fieldOrder: [...(s.fieldOrder ?? []), key],
    }));
    setDirty(true);
    setAddMenuOpen(false);
  };

  /** グループ追加（プリセットから一括展開） */
  const addGroup = (presetId: string) => {
    const preset = BACKGROUND_GROUP_PRESETS[presetId];
    if (!preset) return;
    const existingCount = Object.values(metaSchema.groups ?? {}).filter(
      (g) => g.presetId === presetId,
    ).length;
    const groupLabel = `${preset.label}${existingCount + 1}`;
    const expanded = expandGroupPreset(presetId, groupLabel);
    if (!expanded) return;
    setMeta((m) => {
      const next = { ...m };
      for (const f of expanded.fields) next[f.key] = f.initialValue;
      return next;
    });
    setMetaSchema((s) => ({
      ...s,
      groups: { ...(s.groups ?? {}), [expanded.groupId]: expanded.groupMeta },
      groupOrder: [...(s.groupOrder ?? []), expanded.groupId],
      fields: {
        ...(s.fields ?? {}),
        ...Object.fromEntries(expanded.fields.map((f) => [f.key, f.meta])),
      },
    }));
    setDirty(true);
    setAddMenuOpen(false);
  };

  const removeGroup = (groupId: string) => {
    const keysInGroup = Object.keys(metaSchema.fields ?? {}).filter(
      (k) => metaSchema.fields?.[k]?.groupId === groupId,
    );
    setMeta((m) => {
      const next = { ...m };
      for (const k of keysInGroup) delete next[k];
      return next;
    });
    setMetaSchema((s) => {
      const newFields = { ...(s.fields ?? {}) };
      for (const k of keysInGroup) delete newFields[k];
      const newGroups = { ...(s.groups ?? {}) };
      delete newGroups[groupId];
      return {
        ...s,
        groups: newGroups,
        groupOrder: (s.groupOrder ?? []).filter((g) => g !== groupId),
        fields: newFields,
      };
    });
    setDirty(true);
  };

  const renameGroup = (groupId: string, label: string) => {
    setMetaSchema((s) => ({
      ...s,
      groups: {
        ...(s.groups ?? {}),
        [groupId]: { ...(s.groups?.[groupId] ?? { label: '' }), label },
      },
    }));
    setDirty(true);
  };

  // 初回ロード時のみ初期化（無限ループ防止のため、後続の useQuery 更新は無視）
  const initialized = useRef(false);

  const handleDeleteNode = useCallback((elId: string) => {
    setNodes((ns) => ns.filter((n) => n.id !== elId));
    setEdges((es) =>
      es.filter((e) => e.source !== elId && e.target !== elId),
    );
    setDirty(true);
  }, []);

  const handleOpenNode = useCallback((elId: string) => {
    setDrawerOpenId(elId);
  }, []);

  useEffect(() => {
    if (!campaign || initialized.current) return;
    setName(campaign.name);
    // background_info を優先、無ければ旧固定フィールドから移行（並走期間の互換維持）
    const bg: CampaignBackgroundInfo = {
      ...createEmptyBackgroundInfo(),
      // 旧フィールドから取り込み（background_info 未設定 doc 用）
      description: campaign.description ?? '',
      goal: campaign.goal ?? '',
      targetSegment: campaign.targetSegment ?? '',
      budget: campaign.budget ?? null,
      kpi: campaign.kpi ?? '',
      owner: campaign.owner ?? '',
      memo: campaign.memo ?? '',
      // background_info があれば上書き
      ...(campaign.background_info ?? {}),
    };
    setMeta(bg);
    setMetaSchema(campaign.background_info_schema ?? {});
    const handlers = {
      onOpen: handleOpenNode,
      onDelete: handleDeleteNode,
    };
    setNodes(elementsToNodes(campaign.elements ?? [], handlers));
    setEdges(connectionsToEdges(campaign.connections ?? []));
    initialized.current = true;
  }, [campaign, handleOpenNode, handleDeleteNode]);

  const onNodesChange = useCallback((changes: NodeChange[]) => {
    setNodes((ns) => applyNodeChanges(changes, ns));
    if (
      changes.some(
        (c) => c.type === 'position' || c.type === 'remove' || c.type === 'add',
      )
    ) {
      setDirty(true);
    }
  }, []);

  const onEdgesChange = useCallback((changes: EdgeChange[]) => {
    setEdges((es) => applyEdgeChanges(changes, es));
    if (changes.some((c) => c.type === 'remove' || c.type === 'add')) {
      setDirty(true);
    }
  }, []);

  const onConnect = useCallback(
    (connection: Connection) => {
      const newEdge: Edge = {
        ...connection,
        id: genId(),
        source: connection.source ?? '',
        target: connection.target ?? '',
        label: 'on_complete',
        labelStyle: { fontSize: 10, fill: 'var(--text-muted)' },
        labelBgStyle: { fill: 'var(--bg-elevated)' },
        style: { stroke: 'var(--text-muted)' },
        data: {
          trigger: { kind: 'on_complete' },
          condition: undefined,
          label: undefined,
        },
      };
      setEdges((es) => addEdge(newEdge, es));
      setDirty(true);
    },
    [],
  );

  const handleAddCard = (type: CampaignElementType) => {
    // 既存カードの右側に並べる（カード幅 360 + 40px マージン）
    // 既存カードが無い場合は (200, 200) に初期配置
    const newPos =
      nodes.length === 0
        ? { x: 200, y: 200 }
        : {
            x: Math.max(...nodes.map((n) => n.position.x)) + 400,
            y: 200 + (nodes.length % 4) * 60,
          };
    const newEl: CampaignElement = {
      id: genId(),
      type,
      x: newPos.x,
      y: newPos.y,
    };
    setNodes((ns) => [
      // 既存カードの selected をクリア (複数選択状態だと React Flow が
      // 全選択ノードを一括ドラッグするバグの原因になる)
      ...ns.map((n) => (n.selected ? { ...n, selected: false } : n)),
      {
        id: newEl.id,
        type: 'campaign',
        position: { x: newEl.x, y: newEl.y },
        data: {
          element: newEl,
          onOpen: handleOpenNode,
          onDelete: handleDeleteNode,
        },
        // 新規追加カードのみ選択状態にして、視覚的に判別しやすくする
        selected: true,
      },
    ]);
    setDirty(true);
  };

  const handleApplyElement = useCallback((next: CampaignElement) => {
    setNodes((ns) =>
      ns.map((n) =>
        n.id === next.id
          ? {
              ...n,
              data: {
                ...(n.data as Record<string, unknown>),
                element: { ...next, x: n.position.x, y: n.position.y },
              },
            }
          : n,
      ),
    );
    setDirty(true);
  }, []);

  const drawerElement: CampaignElement | null = (() => {
    if (!drawerOpenId) return null;
    const node = nodes.find((n) => n.id === drawerOpenId);
    if (!node) return null;
    return (node.data as { element: CampaignElement }).element;
  })();

  const drawerConnection: CampaignConnection | null = (() => {
    if (!edgeDrawerOpenId) return null;
    const e = edges.find((x) => x.id === edgeDrawerOpenId);
    if (!e) return null;
    const data = (e.data ?? {}) as Partial<
      Pick<CampaignConnection, 'trigger' | 'condition' | 'label'>
    >;
    return {
      id: e.id,
      fromId: e.source,
      toId: e.target,
      fromHandle: (e.sourceHandle as 'left' | 'right' | undefined) ?? 'right',
      toHandle: (e.targetHandle as 'left' | 'right' | undefined) ?? 'left',
      trigger: data.trigger ?? { kind: 'on_complete' },
      condition: data.condition,
      label: data.label,
    };
  })();

  const handleApplyConnection = useCallback(
    (next: CampaignConnection) => {
      setEdges((es) =>
        es.map((e) =>
          e.id === next.id
            ? {
                ...e,
                label: edgeLabelFor(next),
                data: {
                  trigger: next.trigger,
                  condition: next.condition,
                  label: next.label,
                },
              }
            : e,
        ),
      );
      setDirty(true);
    },
    [],
  );

  const handleDeleteConnection = useCallback((connId: string) => {
    setEdges((es) => es.filter((e) => e.id !== connId));
    setDirty(true);
  }, []);

  const onEdgeClick = useCallback((_e: unknown, edge: Edge) => {
    setEdgeDrawerOpenId(edge.id);
  }, []);

  const prevElements = useMemo(
    () => campaign?.elements ?? [],
    [campaign?.elements],
  );

  // CardDetailDrawer に渡す「現在ボード上の最新 elements / connections」（保存前でも反映）。
  // CTA URL の自動推定（接続先 landing/form の URL 算出）に使われる。
  const liveElements = useMemo(
    () => nodesToElements(nodes, prevElements),
    [nodes, prevElements],
  );
  const liveConnections = useMemo(() => edgesToConnections(edges), [edges]);

  const handleSave = async () => {
    if (!id) return;
    try {
      // React Flow 内部 state を真の値として参照（外部 useState のズレに耐性を持たせる）
      // useReactFlow().getNodes()/getEdges() は最新の内部 state を返す。
      // 外部 state と一致するのが通常だが、 JS dispatch やプログラム的接続線追加で
      // ズレるケースを救済する。
      const internalNodes = rf.getNodes();
      const internalEdges = rf.getEdges();
      const nodesForSave = internalNodes.length > 0 ? internalNodes : nodes;
      const edgesForSave =
        internalEdges.length >= edges.length ? internalEdges : edges;

      const elements = nodesToElements(nodesForSave, prevElements).map((e, i) => {
        // メモが node.data に追加されている場合に拾う（Phase 23a は読取のみ）
        const node = nodesForSave[i];
        const dataEl = (node.data as { element: CampaignElement } | undefined)?.element;
        return dataEl ? { ...dataEl, x: node.position.x, y: node.position.y } : e;
      });
      const connections = edgesToConnections(edgesForSave);

      // 接続線の lint: 未実装 trigger / target カードを警告（保存は止めない）
      const NOT_IMPLEMENTED_TRIGGERS: ReadonlyArray<string> = ['manual'];
      const SUPPORTED_TARGET_TYPES: ReadonlyArray<string> = [
        'mail_schedule',
        'mail_send',
        'line_news',
      ];
      const lintWarnings: string[] = [];
      for (const conn of connections) {
        const trigKind = conn.trigger?.kind;
        if (trigKind && NOT_IMPLEMENTED_TRIGGERS.includes(trigKind)) {
          lintWarnings.push(
            `「${conn.label ?? '無名接続'}」の発火タイミング (${trigKind}) は現在未実装です`,
          );
        }
        const target = elements.find((el) => el.id === conn.toId);
        if (target && !SUPPORTED_TARGET_TYPES.includes(target.type)) {
          lintWarnings.push(
            `「${conn.label ?? '無名接続'}」の到達カード (${target.type}) は接続線では動作しません（ログのみ）`,
          );
        }
      }
      if (lintWarnings.length > 0) {
        toast.warning(
          `接続線に注意点 ${lintWarnings.length} 件:\n${lintWarnings.slice(0, 3).join('\n')}${lintWarnings.length > 3 ? `\n…他 ${lintWarnings.length - 3} 件` : ''}`,
          { duration: 8000 },
        );
      }

      await updateMutation.mutateAsync({
        id,
        data: {
          name,
          elements,
          connections,
          // 新構造（Phase 0）: background_info に集約して保存
          background_info: meta,
          background_info_schema: metaSchema,
          // 旧フィールド並走（既存読み込みコードの互換維持、後で削除）
          description: typeof meta.description === 'string' ? meta.description : '',
          goal: typeof meta.goal === 'string' ? meta.goal : '',
          targetSegment:
            typeof meta.targetSegment === 'string' ? meta.targetSegment : '',
          budget:
            typeof meta.budget === 'number' || meta.budget === null
              ? (meta.budget as number | null)
              : null,
          kpi: typeof meta.kpi === 'string' ? meta.kpi : '',
          owner: typeof meta.owner === 'string' ? meta.owner : '',
          memo: typeof meta.memo === 'string' ? meta.memo : '',
        },
      });
      setSavedAt(Date.now());
      setDirty(false);
      toast.success('保存しました');
    } catch (err) {
      toast.error(`保存失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleSaveAsTemplate = async () => {
    if (!id || !campaign) return;
    if (campaign.isTemplate) {
      toast.error('このキャンペーンは既にテンプレートです');
      return;
    }
    if (
      !window.confirm(
        `現在の状態をテンプレートに変換します（再編集は通常通り可能、テンプレート一覧に表示されます）。よろしいですか？`,
      )
    )
      return;
    setSavingTemplate(true);
    try {
      // まず未保存変更を含めて save、その後 isTemplate=true に変える
      await handleSave();
      await updateMutation.mutateAsync({
        id,
        data: { isTemplate: true, status: 'archived' },
      });
      toast.success('テンプレートとして保存しました');
      navigate('/campaigns');
    } catch (err) {
      toast.error(
        `テンプレート化失敗: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleDeleteCampaign = async () => {
    if (!id) return;
    if (
      !window.confirm(
        `キャンペーン「${name}」を削除します。各リソースの campaignIds から自動的に除去されます。よろしいですか？`,
      )
    )
      return;
    try {
      await deleteMutation.mutateAsync(id);
      toast.success('キャンペーンを削除しました');
      navigate('/campaigns');
    } catch (err) {
      toast.error(`削除失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  // 稼働/休止トグル。即時保存（ボード本体の dirty とは独立）。
  // 既存 campaign（paused undefined）は dispatch.ts の挙動と合わせて「稼働中」として扱う。
  // 新規作成時のみ explicit true がセットされる。
  const isPaused = campaign?.paused === true;
  const handleTogglePaused = async () => {
    if (!id) return;
    const nextPaused = !isPaused;
    if (
      nextPaused === false &&
      !window.confirm(
        '稼働中にすると、設定された全ての自動トリガー（イベント時刻 / フォーム送信 / メール完了 / 遅延ディスパッチ）が発火可能になります。続行しますか？',
      )
    )
      return;
    try {
      await updateMutation.mutateAsync({
        id,
        data: { paused: nextPaused },
      });
      toast.success(nextPaused ? '計画中に切り替えました' : '稼働中に切り替えました');
    } catch (err) {
      toast.error(
        `切替失敗: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  };

  if (isLoading || !campaign) {
    return (
      <div className="flex items-center justify-center p-12">
        <Spinner />
      </div>
    );
  }

  return (
    <Screen
      title="キャンペーンボード"
      description="カードクリックで既存リソースを紐付け。線の自動発火は到達カードが メール配信予約 / メール送信 / LINE ニュース の場合のみ動作。それ以外（ランディング / 資料請求 / 個別説明申込 等）はログのみ。"
      backTo="/campaigns"
      actions={
        <>
          <Button
            type="button"
            variant={isPaused ? 'ghost' : 'primary'}
            size="sm"
            onClick={handleTogglePaused}
            title={
              isPaused
                ? '稼働中に切り替える（自動トリガーが有効になる）'
                : '計画中に切り替える（全ての自動処理がスキップされる）'
            }
            className={
              isPaused
                ? 'border border-[var(--border)] text-[var(--text-muted)]'
                : ''
            }
          >
            {isPaused ? (
              <>
                <Pause className="size-3.5 mr-1" />
                計画中
              </>
            ) : (
              <>
                <Play className="size-3.5 mr-1" />
                稼働中
              </>
            )}
          </Button>
          <AddCardMenu onAdd={handleAddCard} />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleSaveAsTemplate}
            disabled={savingTemplate || !!campaign?.isTemplate}
            title={
              campaign?.isTemplate
                ? '既にテンプレート化されています'
                : '現在の状態をテンプレートとして保存'
            }
          >
            {savingTemplate ? (
              <Spinner className="size-3 mr-1" />
            ) : (
              <Bookmark className="size-3.5 mr-1" />
            )}
            テンプレ化
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={handleSave}
            disabled={!dirty || updateMutation.isPending}
          >
            {updateMutation.isPending ? (
              <Spinner className="size-3 mr-1" />
            ) : (
              <Save className="size-3.5 mr-1" />
            )}
            {dirty ? '保存' : '保存済'}
          </Button>
          <Button
            type="button"
            variant="danger"
            size="sm"
            onClick={handleDeleteCampaign}
          >
            <Trash2 className="size-3.5 mr-1" />
            削除
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Input
            type="text"
            placeholder="キャンペーン名"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setDirty(true);
            }}
            className="flex-1 min-w-[200px]"
          />
          {campaign?.isTemplate && (
            <Badge variant="accent">テンプレート</Badge>
          )}
          {savedAt && !dirty && (
            <span className="text-[10px] text-[var(--text-muted)]">
              最終保存: {new Date(savedAt).toLocaleTimeString('ja-JP')}
            </span>
          )}
        </div>

        <Tabs defaultValue="board" className="space-y-3">
          <TabsList className="mb-0">
            <TabsTrigger value="board">キャンペーンボード</TabsTrigger>
            <TabsTrigger value="monitoring">モニタリング</TabsTrigger>
          </TabsList>

          <TabsContent value="monitoring">
            {id && campaign ? (
              <CampaignMonitoringView campaignId={id} campaign={campaign} />
            ) : (
              <div className="flex items-center justify-center p-12">
                <Spinner />
              </div>
            )}
          </TabsContent>

          <TabsContent
            value="board"
            forceMount
            className="data-[state=inactive]:hidden"
          >
        <div
          className="flex gap-3"
          style={{ height: 'calc(100vh - 320px)', minHeight: 480 }}
        >
          {/* ボード本体 */}
          <div className="flex-1 rounded border border-[var(--border)] overflow-hidden">
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onEdgeClick={onEdgeClick}
              nodeTypes={NODE_TYPES}
              fitView
              multiSelectionKeyCode="Meta"
              deleteKeyCode={['Backspace', 'Delete']}
              proOptions={{ hideAttribution: true }}
              // Phase 23f: 10 枚以上で可視領域のみレンダリング（仮想化）
              // 10 枚未満は通常レンダリング（既存挙動）
              onlyRenderVisibleElements={nodes.length >= 10}
              // 軽量化: 10 枚以上では Background のドット間隔を粗くしてGPUコストを削減
              // また minSelectionDistance を上げて誤選択を減らす
              minZoom={nodes.length >= 10 ? 0.2 : 0.5}
              maxZoom={2}
            >
              <Background
                gap={nodes.length >= 10 ? 24 : 16}
                size={1}
                color="var(--border)"
              />
              <Controls />
              <MiniMap
                nodeColor="var(--accent)"
                maskColor="rgba(0,0,0,0.1)"
                // 10 枚以上ではミニマップを常に表示してナビゲーションしやすく
                pannable={nodes.length >= 10}
                zoomable={nodes.length >= 10}
                style={{
                  backgroundColor: 'var(--bg-elevated)',
                  border: '1px solid var(--border)',
                }}
              />
            </ReactFlow>
          </div>

          {/* 右側：折りたたみ式メタデータパネル */}
          <div
            className={`rounded border border-[var(--border)] bg-[var(--bg-surface)] flex flex-col transition-all overflow-hidden ${
              sidePanelOpen
                ? panelWide
                  ? 'w-[480px]'
                  : 'w-[360px]'
                : 'w-[40px]'
            }`}
          >
            <div className="flex items-center border-b border-[var(--border)]">
              <button
                type="button"
                onClick={() => setSidePanelOpen((s) => !s)}
                className="flex-1 flex items-center justify-between px-3 py-2 hover:bg-[var(--bg-hover)] transition-colors"
                title={sidePanelOpen ? '折りたたむ' : '展開する'}
              >
                {sidePanelOpen ? (
                  <>
                    <span className="text-[12px] font-semibold">
                      キャンペーン詳細
                    </span>
                    <ChevronRight className="size-4 text-[var(--text-muted)]" />
                  </>
                ) : (
                  <ChevronLeft className="size-4 text-[var(--text-muted)] mx-auto" />
                )}
              </button>
              {sidePanelOpen && (
                <button
                  type="button"
                  onClick={() => setPanelWide((w) => !w)}
                  className="px-2 py-2 border-l border-[var(--border)] hover:bg-[var(--bg-hover)] text-[var(--text-muted)]"
                  title={panelWide ? '通常幅に戻す' : 'パネルを広げる'}
                >
                  {panelWide ? (
                    <Minimize2 className="size-3.5" />
                  ) : (
                    <Maximize2 className="size-3.5" />
                  )}
                </button>
              )}
            </div>

            {sidePanelOpen && (
              <div className="overflow-y-auto p-3 space-y-4 flex-1">
                {/* ─── 既定フィールド ─── */}
                <section className="space-y-3">
                  {CAMPAIGN_BACKGROUND_DEFAULT_KEYS.filter((k) => k in meta).map(
                    (key) => {
                      const value = meta[key];
                      const label = getCampaignBackgroundLabel(key);
                      const placeholder =
                        CAMPAIGN_BACKGROUND_PLACEHOLDERS[
                          key as keyof typeof CAMPAIGN_BACKGROUND_PLACEHOLDERS
                        ];
                      const isNumberField = key === 'budget';
                      const isLongText = [
                        'description',
                        'goal',
                        'targetSegment',
                        'kpi',
                        'memo',
                      ].includes(key);
                      return (
                        <div key={key}>
                          <Label className="text-xs mb-1 block">{label}</Label>
                          {isNumberField ? (
                            <>
                              <Input
                                type="number"
                                min="0"
                                step="1000"
                                value={
                                  value === null || value === undefined
                                    ? ''
                                    : String(value)
                                }
                                onChange={(e) => {
                                  const v = e.target.value;
                                  updateMetaField(
                                    key,
                                    v === '' ? null : Number.parseInt(v, 10),
                                  );
                                }}
                                placeholder={placeholder}
                              />
                              {typeof value === 'number' && value > 0 && (
                                <p className="text-[10px] text-[var(--text-muted)] mt-0.5">
                                  ¥ {value.toLocaleString('ja-JP')}
                                </p>
                              )}
                            </>
                          ) : isLongText ? (
                            <Textarea
                              value={typeof value === 'string' ? value : ''}
                              onChange={(e) =>
                                updateMetaField(key, e.target.value)
                              }
                              rows={key === 'memo' ? 3 : 2}
                              placeholder={placeholder}
                            />
                          ) : (
                            <Input
                              type="text"
                              value={
                                value === null || value === undefined
                                  ? ''
                                  : String(value)
                              }
                              onChange={(e) =>
                                updateMetaField(key, e.target.value)
                              }
                              placeholder={placeholder}
                            />
                          )}
                        </div>
                      );
                    },
                  )}
                </section>

                {/* ─── 任意フィールド（standalone） ─── */}
                {standaloneCustomKeys(meta, metaSchema).length > 0 && (
                  <section className="space-y-3 pt-1 border-t border-[var(--border)]">
                    <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] pt-2">
                      任意フィールド
                    </div>
                    {standaloneCustomKeys(meta, metaSchema).map((key) => {
                      const value = meta[key];
                      const fieldMeta = metaSchema.fields?.[key];
                      const type: BackgroundFieldType = fieldMeta?.type ?? 'text';
                      const label = resolveBackgroundLabel(key, metaSchema);
                      return (
                        <div key={key} className="space-y-1">
                          <div className="flex items-center gap-1">
                            <Input
                              type="text"
                              value={label}
                              onChange={(e) => renameField(key, e.target.value)}
                              className="text-xs flex-1 h-7 px-2 bg-transparent"
                              placeholder="フィールド名（日本語可）"
                            />
                            <button
                              type="button"
                              onClick={() => removeMetaField(key)}
                              className="text-[12px] text-[var(--text-muted)] hover:text-[var(--accent-red)] px-1"
                              title="このフィールドを削除"
                            >
                              ✕
                            </button>
                          </div>
                          {type === 'number' ? (
                            <Input
                              type="number"
                              value={
                                value === null || value === undefined
                                  ? ''
                                  : String(value)
                              }
                              onChange={(e) => {
                                const v = e.target.value;
                                updateMetaField(
                                  key,
                                  v === '' ? null : Number.parseFloat(v),
                                );
                              }}
                            />
                          ) : type === 'long' ? (
                            <Textarea
                              value={typeof value === 'string' ? value : ''}
                              onChange={(e) =>
                                updateMetaField(key, e.target.value)
                              }
                              rows={3}
                            />
                          ) : (
                            <Input
                              type={type === 'date' ? 'date' : 'text'}
                              value={
                                value === null || value === undefined
                                  ? ''
                                  : String(value)
                              }
                              onChange={(e) =>
                                updateMetaField(key, e.target.value)
                              }
                            />
                          )}
                        </div>
                      );
                    })}
                  </section>
                )}

                {/* ─── グループ ─── */}
                {(metaSchema.groupOrder ?? []).map((groupId) => {
                  const group = metaSchema.groups?.[groupId];
                  if (!group) return null;
                  const collapsed = collapsedGroups[groupId] ?? false;
                  const keys = groupFieldKeys(meta, metaSchema, groupId);
                  return (
                    <section
                      key={groupId}
                      className="border border-[var(--border)] rounded-md bg-[var(--bg-elevated)]"
                    >
                      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-[var(--border)]">
                        <button
                          type="button"
                          onClick={() =>
                            setCollapsedGroups((c) => ({
                              ...c,
                              [groupId]: !collapsed,
                            }))
                          }
                          className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                          title={collapsed ? '展開' : '折りたたみ'}
                        >
                          {collapsed ? (
                            <ChevronRight className="size-3.5" />
                          ) : (
                            <ChevronDown className="size-3.5" />
                          )}
                        </button>
                        <Input
                          type="text"
                          value={group.label}
                          onChange={(e) => renameGroup(groupId, e.target.value)}
                          className="text-xs font-semibold flex-1 h-7 px-2 bg-transparent border-transparent hover:border-[var(--border)] focus:border-[var(--border)]"
                          placeholder="グループ名"
                        />
                        {group.presetId && (
                          <button
                            type="button"
                            onClick={() => addGroup(group.presetId!)}
                            className="text-[10px] text-[var(--text-muted)] hover:text-[var(--text-primary)] px-1.5 py-0.5 border border-[var(--border)] rounded"
                            title="同じ種類をもう 1 セット追加"
                          >
                            複製
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            if (
                              window.confirm(
                                `グループ「${group.label}」と内部フィールドを削除しますか？`,
                              )
                            ) {
                              removeGroup(groupId);
                            }
                          }}
                          className="text-[12px] text-[var(--text-muted)] hover:text-[var(--accent-red)] px-1"
                          title="グループを削除"
                        >
                          ✕
                        </button>
                      </div>
                      {!collapsed && (
                        <div className="p-2 space-y-2">
                          {keys.map((key) => {
                            const value = meta[key];
                            const fieldMeta = metaSchema.fields?.[key];
                            const type: BackgroundFieldType =
                              fieldMeta?.type ?? 'text';
                            const label = resolveBackgroundLabel(
                              key,
                              metaSchema,
                            );
                            return (
                              <div key={key} className="space-y-1">
                                <div className="flex items-center gap-1">
                                  <Input
                                    type="text"
                                    value={label}
                                    onChange={(e) =>
                                      renameField(key, e.target.value)
                                    }
                                    className="text-xs flex-1 h-6 px-2 bg-transparent border-transparent hover:border-[var(--border)] focus:border-[var(--border)]"
                                    placeholder="項目名"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removeMetaField(key)}
                                    className="text-[11px] text-[var(--text-muted)] hover:text-[var(--accent-red)] px-1"
                                    title="この項目を削除"
                                  >
                                    ✕
                                  </button>
                                </div>
                                {type === 'number' ? (
                                  <Input
                                    type="number"
                                    value={
                                      value === null || value === undefined
                                        ? ''
                                        : String(value)
                                    }
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      updateMetaField(
                                        key,
                                        v === ''
                                          ? null
                                          : Number.parseFloat(v),
                                      );
                                    }}
                                  />
                                ) : type === 'long' ? (
                                  <Textarea
                                    value={
                                      typeof value === 'string' ? value : ''
                                    }
                                    onChange={(e) =>
                                      updateMetaField(key, e.target.value)
                                    }
                                    rows={2}
                                  />
                                ) : (
                                  <Input
                                    type={type === 'date' ? 'date' : 'text'}
                                    value={
                                      value === null || value === undefined
                                        ? ''
                                        : String(value)
                                    }
                                    onChange={(e) =>
                                      updateMetaField(key, e.target.value)
                                    }
                                  />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </section>
                  );
                })}

                {/* ─── + 追加メニュー ─── */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setAddMenuOpen((o) => !o)}
                    className="w-full text-xs text-[var(--text-muted)] border border-dashed border-[var(--border)] rounded-md py-2 hover:bg-[var(--bg-hover)] transition-colors flex items-center justify-center gap-1"
                  >
                    <Plus className="size-3.5" />
                    フィールドまたはグループを追加
                    {addMenuOpen ? (
                      <ChevronUp className="size-3.5" />
                    ) : (
                      <ChevronDown className="size-3.5" />
                    )}
                  </button>
                  {addMenuOpen && (
                    <div className="mt-1 border border-[var(--border)] rounded-md bg-[var(--bg-elevated)] shadow-md overflow-hidden">
                      <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-[var(--text-muted)] bg-[var(--bg-root)]">
                        単一フィールド
                      </div>
                      <button
                        type="button"
                        onClick={() => addStandaloneField('text')}
                        className="w-full text-left px-3 py-1.5 text-xs hover:bg-[var(--bg-hover)]"
                      >
                        テキスト（1 行）
                      </button>
                      <button
                        type="button"
                        onClick={() => addStandaloneField('long')}
                        className="w-full text-left px-3 py-1.5 text-xs hover:bg-[var(--bg-hover)]"
                      >
                        長文テキスト
                      </button>
                      <button
                        type="button"
                        onClick={() => addStandaloneField('number')}
                        className="w-full text-left px-3 py-1.5 text-xs hover:bg-[var(--bg-hover)]"
                      >
                        数値
                      </button>
                      <button
                        type="button"
                        onClick={() => addStandaloneField('date')}
                        className="w-full text-left px-3 py-1.5 text-xs hover:bg-[var(--bg-hover)]"
                      >
                        日付
                      </button>
                      <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-[var(--text-muted)] bg-[var(--bg-root)] border-t border-[var(--border)]">
                        グループ（一括追加）
                      </div>
                      {Object.values(BACKGROUND_GROUP_PRESETS).map((preset) => (
                        <button
                          key={preset.id}
                          type="button"
                          onClick={() => addGroup(preset.id)}
                          className="w-full text-left px-3 py-1.5 text-xs hover:bg-[var(--bg-hover)] flex items-center justify-between"
                        >
                          <span>{preset.label}</span>
                          <span className="text-[10px] text-[var(--text-muted)]">
                            {preset.fields.length} 項目
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {campaign?.fromTemplateId && (
                  <div className="text-[10px] text-[var(--text-muted)] pt-2 border-t border-[var(--border)]">
                    元テンプレート: {campaign.fromTemplateId}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
          </TabsContent>
        </Tabs>
      </div>

      <CardDetailDrawer
        open={drawerOpenId !== null}
        onOpenChange={(o) => {
          if (!o) setDrawerOpenId(null);
        }}
        element={drawerElement}
        campaignId={id ?? ''}
        hasIncoming={
          drawerOpenId
            ? edges.some((e) => e.target === drawerOpenId)
            : false
        }
        allElements={liveElements}
        allConnections={liveConnections}
        onSave={handleApplyElement}
      />

      <ConnectionDetailDrawer
        open={edgeDrawerOpenId !== null}
        onOpenChange={(o) => {
          if (!o) setEdgeDrawerOpenId(null);
        }}
        connection={drawerConnection}
        onSave={handleApplyConnection}
        onDelete={handleDeleteConnection}
        campaignId={id}
      />
    </Screen>
  );
}

export function CampaignBoardPage() {
  return (
    <ReactFlowProvider>
      <CampaignBoardInner />
    </ReactFlowProvider>
  );
}
